export interface AuthResponse {
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
  user?: UserData;
  error?: string;
}

export interface UserData {
  userId: number;
  username: string;
  firstName: string;
  lastName: string;
  userToken: string;
  permissions: string[];
  roles?: string[];
  personalData?: {
    firstName: string;
    lastName: string;
    [key: string]: any;
  };
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// CRM Contact schemas
export interface ContactPhoneNumberRead {
  id: number;
  contact_id: number;
  phone_number: string;
  is_default?: boolean;
  has_whatsapp?: boolean;
}

export interface ContactPhoneNumberCreate {
  phone_number: string;
  is_default?: boolean;
  has_whatsapp?: boolean;
}

export interface ContactRead {
  id: string;
  given_name: string | null;
  family_name: string | null;
  email: string | null;
  national_id_series?: string | null;
  national_id_number?: string | null;
  cnp?: string | null;
  bank_account?: string | null;
  bank_account_holder?: string | null;
  phone_numbers: ContactPhoneNumberRead[];
  quotes_count?: number;
  addresses?: AddressRead[];
}

export interface ContactCreate {
  given_name?: string;
  family_name?: string;
  email?: string;
  source_id?: number;
  source_code?: string;
  phone_numbers?: ContactPhoneNumberCreate[];
  national_id_series?: string;
  national_id_number?: string;
  cnp?: string;
  bank_account?: string;
  bank_account_holder?: string;
}

// Contact merge request types
export interface ContactMergeIncludeModules {
  quotes?: boolean;
  addresses?: boolean;
  companies?: boolean;
  phones?: boolean;
}

export interface ContactMergeFieldResolution {
  given_name?: string | null;
  family_name?: string | null;
  email?: string | null;
  national_id_series?: string | null;
  national_id_number?: string | null;
  cnp?: string | null;
  bank_account?: string | null;
  bank_account_holder?: string | null;
  default_phone_number?: string | null; // E.164
}

export interface ContactMergeRequest {
  target_contact_hash: string;
  source_contact_hash: string;
  field_resolution?: ContactMergeFieldResolution;
  include_modules?: ContactMergeIncludeModules;
}

// CRM Company schemas
export interface CompanyRead {
  id: string;
  name: string;
  vat_id: string | null;
  is_tax_payer: boolean;
  registration_number: string | null;
  address_string: string | null;
  contacts: ContactRead[];
}

export interface CompanyCreate {
  name: string;
  vat_id?: string;
  is_tax_payer?: boolean;
  registration_number?: string;
  address_string?: string;
}

export interface CompanyUpdate {
  name?: string;
  vat_id?: string;
  is_tax_payer?: boolean;
  registration_number?: string;
  address_string?: string;
}

export interface PaginatedCompanies {
  total: number;
  items: CompanyRead[];
  limit: number;
  offset: number;
}

export type ContactsByQuotesResponse = {
  [quoteHash: string]: ContactRead;
};

export interface ContactCompanyLinkCreate {
  contact_id?: string;
  company_id?: string;
}

export interface ContactCompanyLinkRead {
  id: number;
  contact_id: string;
  company_id: string;
  contact?: ContactRead;
  company?: CompanyRead;
}

// CRM Quote schemas
// Base interface for common LineItem fields
export interface LineItemBase {
  component_id?: number | null;
  sku?: string | null;
  quantity?: number;
  price?: number | null;
  currency_id?: number; // Backend currency FK (1=RON,2=USD,3=EUR)
  defects?: number; // Percentage 0-100
  available?: boolean | null;
  line_item_type?: 'part_request' | 'vehicle_sale' | 'kran_service_request';
  comments?: LineItemComment[];
  invoice_note?: string | null; // Invoice-specific note prepended to product description
  defect_note?: string | null; // Notes shown to the customer on the order-confirmation page
}

// Read interface that extends base with read-specific fields
export interface LineItemRead extends LineItemBase {
  id: number;
  component?: string | null;
  created_at: string;
  quantity: number; // Required for reads
  defects: number; // Required for reads
  currency_id?: number;
}

export interface LineItemStockLocationRead {
  id_location: number;
  warehouse?: string | null;
  warehouse_area_id?: number | null;
  warehouse_area?: string | null;
  location?: string | null;
  quantity: number;
}

export interface LineItemFullRead extends LineItemRead {
  vehicle: CustomerVehicleRead | null;
  images: LineItemImageRead[];
  stock_locations?: LineItemStockLocationRead[];
}

// Extended LineItem row for list endpoints, including related entity ids
export interface LineItemWithRelations extends LineItemFullRead {
  quotes: string[];  // hashed ids
  sales: string[];   // hashed ids
  returns: string[]; // hashed ids
  tasks: number[];   // plain numeric ids
}

export interface PaginatedLineItemsWithRelations {
  total: number;
  items: LineItemWithRelations[];
  limit: number;
  offset: number;
}

// Params for Line Items list endpoint
export interface LineItemsListParams {
  limit?: number;
  offset?: number;
  available?: boolean | string;
  created_from?: string;
  created_to?: string;
  sort_by?: 'created_at' | 'updated_at' | 'price';
  sort_order?: 'asc' | 'desc';
  count_only?: boolean;
  include_details?: boolean;
  q?: string; // Free-text search across component/vehicle/comments
  record_types?: string; // Comma-separated: quote,sale,return,task (absent = all)
}

// Enhanced LineItem with details (when include_details=true)
export interface LineItemComment {
  created_by_username: string;
  comment_text: string;
  comment_type?: 'comment' | 'action';
  created_at?: string;
  created_by_user_id?: number;
  created_by_role?: 'owner' | 'admin' | 'call_center' | 'reception' | 'field_agent' | 'orders' | 'system' | 'returns_manager';
}

export interface LineItemImage {
  url: string;
  image_type: 'customer' | 'our';
  created_at?: string;
  created_by_username?: string;
  created_by_user_id?: number | null;
  created_by_role?: 'owner' | 'admin' | 'call_center' | 'reception' | 'field_agent' | 'orders' | 'system' | 'returns_manager';
}

export interface LineItemVehicle {
  id: number;
  model_id?: number;
  manufacturer_id?: number;
  body_type?: string;
  vin_number?: string;
  build_year?: number;
  comments?: string;
  images?: LineItemImage[];
  // Additional fields that might be useful for display
  make?: string;
  model?: string;
  engine_code?: string;
  engine?: string;
}

export interface LineItemHistorical {
  lastPrice?: number;
  requestCount?: number;
  lastRequestDate?: string; // This is a string because it comes as an ISO date
  lastStatus?: string;
  currency_id?: number;
}

export interface LineItemCompatibilitySet {
  id: number;
  type: string;
  tag?: string | null;
  notes?: string | null;
  models: string[];
  files?: Array<{
    url: string;
    created_at?: string | null;
    created_by_username?: string | null;
    created_by_role?: string | null;
  }>;
}

export interface LineItemCompatibilitySummary {
  count: number;
  sets: LineItemCompatibilitySet[];
}

export interface LineItemHistoricalData {
  actual_stock: number;
  historical: LineItemHistorical[];
  last_vehicle_purchased_date?: string; // ISO date string
  notes?: string | null;
  compatibility_models?: LineItemCompatibilitySummary;
}

export type HistoricalStockResponse = Record<string, LineItemHistoricalData>;

export interface LineItemSummarySearchHit {
  component_id: number;
  model_id: number;
  label: string;
}

export interface LineItemSummarySearchResponse {
  items: LineItemSummarySearchHit[];
}

export interface LineItemCreate extends LineItemBase {
  quantity: number; // Required for creation
  price: number; // Required for creation
  defects?: number; // Optional for creation, defaults to 0
}

export interface LineItemUpdate extends LineItemBase {
  // All fields are optional for updates
  // Additional update-specific fields
  name?: string;
  currency?: string;
  currency_id?: number;
  uzura?: number;
  total?: number;
}

export interface QuoteRead {
  id: string;
  contact_id?: string | null;
  status: string;
  created_by_user_id: number;
  created_at: string;
  updated_at: string;
  line_items: LineItemFullRead[];
  comments?: LineItemComment[];
  contact?: ContactRead;
  company?: CompanyRead;
  // Additional fields from the enhanced API response
  origin_url?: string;
  source_id?: number | null;
  source?: string | null;
  number?: string;
  total?: number;
  profitabilityScore?: number;
}

// --- Public Links ---
export interface PublicAccessLinkRead {
  id: number;
  token: string;
  scopes?: string[] | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  submitted_at?: string | null;
  uses_count: number;
  last_used_at?: string | null;
  created_at: string;
  label?: string | null;
  link_url?: string | null;
}

export interface PaginatedPublicLinks {
  total: number;
  items: PublicAccessLinkRead[];
  limit: number;
  offset: number;
}

export interface QuoteCreate {
  contact_id: number;
  status?: string;
  line_items?: LineItemCreate[];
}

// CRM Comment schemas
export interface CommentRead {
  id: number;
  line_item_id: number;
  created_by_username: string;
  comment_text: string;
  created_at: string;
  comment_type: 'comment' | 'action';
  created_by_user_id?: number;
  created_by_role?: 'owner' | 'admin' | 'call_center' | 'reception' | 'field_agent' | 'orders' | 'system' | 'returns_manager';
}

export interface CommentCreate {
  comment_id?: string;
  mentions?: Array<{ kind: 'user' | 'role'; id: number }>;
  mention_source?: { module: 'quote' | 'sale' | 'return' | 'task' | 'line_item' | 'kran_quote' | 'kran_contract'; record_id: string };
  comment_text: string;
  comment_type?: 'comment' | 'action';
  created_by_username?: string;
  created_by_user_id?: number;
  created_by_role?: 'owner' | 'admin' | 'call_center' | 'reception' | 'field_agent' | 'orders' | 'system' | 'returns_manager';
}

// CRM Line Item Image schemas - now stored in JSON column
export interface LineItemImageRead {
  image_type: 'customer' | 'our';
  url: string;
  created_at?: string;
  created_by_username?: string;
  created_by_user_id?: number | null;
  created_by_role?: 'owner' | 'admin' | 'call_center' | 'reception' | 'field_agent' | 'orders' | 'system' | 'returns_manager';
  // Optional media fields (videos go through a background transcoding worker)
  media_type?: 'image' | 'video';
  status?: 'transcoding' | 'ready' | 'failed';
  entry_id?: string;
  poster_url?: string | null;
  small_url?: string | null;
}

// CRM Vehicle schemas
export interface CustomerVehicleRead {
  id: string;
  contact_id: string;
  body_type?: string;
  vin_number?: string;
  comments?: string;
  build_year?: number;
  model?: string | { idModel?: number; model?: string; internal_model_notes?: string | null };
  manufacturer?: string | { manufacturer?: string; notes?: string | null };
  engine_code?: string | { engineCode?: string };
  engine?: string | { engine?: string };
  engine_type?: string | { engineType?: string };
  created_at?: string;
  updated_at?: string;
  images?: VehicleImageRead[];  // Add images array
}

export interface CustomerVehicleCreate {
  contact_id: string;
  manufacturer_id?: number;
  model_id?: number;
  engine_id?: number;
  engine_code_id?: number;
  engine_type_id?: number;
  build_year?: number;
  vin_number?: string;
  body_type?: string;
  comments?: string;
}

export interface CustomerVehicleUpdate {
  manufacturer_id?: number;
  model_id?: number;
  engine_id?: number;
  engine_code_id?: number;
  engine_type_id?: number;
  build_year?: number;
  vin_number?: string;
  body_type?: string;
  comments?: string;
}

// CRM Vehicle Image schemas - now stored in JSON column
export interface VehicleImageRead {
  image_type: 'customer' | 'our';
  url: string;
  created_at?: string;
  created_by_username?: string;
  created_by_user_id?: number | null;
  created_by_role?: 'owner' | 'admin' | 'call_center' | 'reception' | 'field_agent' | 'orders' | 'system' | 'returns_manager';
  // Optional media fields (videos go through a background transcoding worker)
  media_type?: 'image' | 'video';
  status?: 'transcoding' | 'ready' | 'failed';
  entry_id?: string;
  poster_url?: string | null;
  small_url?: string | null;
}

// CRM Address schemas
export interface CountyRead {
  idCounty: number;
  county: string;
}

export interface CityRead {
  idCity: number;
  city: string;
}

export interface AddressRead {
  id: string;
  contact_id: string;
  id_county: number | null;
  id_city: number | null;
  street_name: string | null;
  street_number: string | null;
  block_name: string | null;
  floor: string | null;
  apartment: string | null;
  postal_code: string | null;
  country: string | null;
  additional_info: string | null;
  freeform_address: string | null;
  google_maps_url: string | null;
  latitude: number | null;
  longitude: number | null;
  county?: CountyRead;
  city?: CityRead;
}

export interface AddressCreate {
  contact_id: string;
  id_county?: number;
  id_city?: number;
  street_name?: string;
  street_number?: string;
  block_name?: string;
  floor?: string;
  apartment?: string;
  postal_code?: string;
  country?: string;
  additional_info?: string;
  freeform_address?: string;
  google_maps_url?: string;
  latitude?: number;
  longitude?: number;
}

export interface AddressUpdate {
  id_county?: number;
  id_city?: number;
  street_name?: string;
  street_number?: string;
  block_name?: string;
  floor?: string;
  apartment?: string;
  postal_code?: string;
  country?: string;
  additional_info?: string;
  freeform_address?: string;
  google_maps_url?: string;
  latitude?: number;
  longitude?: number;
}

export interface ResolveGoogleMapsLinkResponse {
  original_url: string;
  resolved_url?: string | null;
  latitude: number;
  longitude: number;
  place_id?: string | null;
  display_name?: string | null;
  formatted_address?: string | null;
  google_maps_uri?: string | null;
  resolution_method: string;
}

// CRM Task schemas
export interface TaskCreate {
  description?: string;
  status?: TaskStatus;
  priority?: 'low' | 'normal' | 'high';
  type?: 'part_verification' | 'disassembly_reception' | 'disassembly_order' | 'packing' | 'vehicle_acquisition' | 'delivery_exception' | 'photo_request';
  created_by_user_id?: number;
  assigned_to_user_id?: number;
  assigned_to_role_id?: number;
  line_item_ids?: number[];
  sale_id?: string; // hash ID
  quote_id?: string; // hash ID
}

export interface SimpleTaskRead {
  id: number;
  status: string;
  line_item_ids?: number[];
}

// Create task directly from SKUs
export interface SkuTaskItem {
  sku: string;
  component_id: number;
  quantity?: number;
  price?: number;
}

export interface TaskCreateFromSkus {
  description?: string;
  status?: TaskStatus;
  priority?: 'low' | 'normal' | 'high';
  type?: 'part_verification' | 'disassembly_reception' | 'disassembly_order' | 'packing' | 'vehicle_acquisition' | 'delivery_exception' | 'photo_request';
  assigned_to_user_id?: number;
  assigned_to_role_id?: number;
  sale_id?: string; // hash ID
  quote_id?: string; // hash ID
  items: SkuTaskItem[];
}

export interface TaskUpdate {
  description?: string;
  status?: TaskStatus;
  priority?: 'low' | 'normal' | 'high';
  type?: 'part_verification' | 'disassembly_reception' | 'disassembly_order' | 'packing' | 'vehicle_acquisition' | 'delivery_exception' | 'photo_request';
  assigned_to_user_id?: number;
  assigned_to_role_id?: number;
}

export interface TaskRead {
  id: number;
  description?: string;
  status: TaskStatus;
  priority: 'low' | 'normal' | 'high';
  type?: 'part_verification' | 'disassembly_reception' | 'disassembly_order' | 'packing' | 'vehicle_acquisition' | 'delivery_exception' | 'photo_request' | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  created_by_user_id?: number;
  created_by_username?: string;
  assigned_to_user_id?: number | null;
  assigned_to_username?: string;
  assigned_to_role_id?: number | null;
  assigned_to_role_name?: string;
  sale_id?: string | null; // hash ID
  quote_id?: string | null; // hash ID
  line_items: LineItemFullRead[];
}

export interface PaginatedTasks {
  total: number;
  items: TaskRead[];
  limit: number;
  offset: number;
}

// Composite Quote Creation types
export interface CompositeQuoteContact {
  // Option 1: Existing contact
  contact_hash?: string;
  // Option 2: New contact
  given_name?: string;
  family_name?: string;
  email?: string;
  phone_numbers?: string[];
}

export interface CompositeQuoteLocation {
  county_id?: number;
  city_id?: number;
  street_name?: string;
  street_number?: string;
  block_name?: string;
  floor?: string;
  apartment?: string;
  postal_code?: string;
  country?: string;
  additional_info?: string;
  latitude?: number;
  longitude?: number;
}

export interface CompositeQuoteComment {
  created_by_username?: string;
  comment_text: string;
  comment_type?: 'comment' | 'action';
  created_by_user_id?: number;
  created_by_role?: 'owner' | 'admin' | 'call_center' | 'reception' | 'field_agent' | 'orders' | 'system' | 'returns_manager';
}

export interface CompositeQuoteLineItem {
  component_id?: number;
  description: string;
  quantity: number;
  price?: number;
  total?: number;
  image_count: number;
  /**
   * Percentage (0-100) representing defects/wear for this line item.
   */
  defects?: number;
  /** SKU (Stock Keeping Unit) identifier for the line item */
  sku?: string;
  /** Availability flag */
  available?: boolean | null;
  /** Line item type */
  line_item_type?: 'part_request' | 'vehicle_sale' | 'kran_service_request';
  /**
   * Optional list of comments/actions attached to this line item.
   */
  comments?: CompositeQuoteComment[];
}

export interface CompositeQuoteVehicle {
  // Option A: Existing vehicle
  vehicle_hash?: string;
  
  // Option B: New vehicle fields
  manufacturer_id?: number;
  model_id?: number;
  engine_code_id?: number;
  engine_id?: number;
  engine_type_id?: number;
  body_type?: string;
  vin_number?: string;
  build_year?: number;
  comments?: string;
  
  // Common fields
  image_count: number;
  line_items: CompositeQuoteLineItem[];
}

export interface CompositeQuoteTask {
  description?: string;
  assigned_to_user_id?: number;
  assigned_to_role_id?: number;
  line_item_indices?: number[];
}

export interface CompositeQuoteRequest {
  contact: CompositeQuoteContact;
  location?: CompositeQuoteLocation;
  origin_url?: string;
  status?: QuoteStatus;
  vehicles: CompositeQuoteVehicle[];
  task?: CompositeQuoteTask;
}

// ==================== NEW TYPES FOR COMPOSITE UPDATE ====================
export interface LineItemUpdatePayload {
  id: number;
  price?: number | null;
  quantity?: number;
  defects?: number;
  component_id?: number | null;
  sku?: string | null;
  available?: boolean;
  line_item_type?: 'part_request' | 'vehicle_sale' | 'kran_service_request';
  comment?: { comment_text: string };
  new_customer_images_count?: number;
  new_dezshop_images_count?: number;
}

export interface NewLineItemPayload {
  vehicle_hash?: string;
  component_id?: number;
  description: string;
  quantity: number;
  price?: number;
  defects?: number;
  sku?: string;
  available?: boolean;
  line_item_type?: 'part_request' | 'vehicle_sale' | 'kran_service_request';
  comment?: { comment_text: string };
  new_customer_images_count?: number;
  new_dezshop_images_count?: number;
}

// Task interface specifically for composite quote updates (matches backend NewTaskPayload)
export interface CompositeUpdateTaskPayload {
  line_item_ids: number[];
  assigned_to_user_id?: number;
  assigned_to_role_id?: number;
  description: string;  // Required
  type: string;         // Required
  priority: string;     // Required
}

export interface QuoteCompositeUpdatePayload {
  line_item_updates?: LineItemUpdatePayload[];
  new_line_items?: NewLineItemPayload[];
  deleted_line_item_ids?: number[];
  task?: CompositeUpdateTaskPayload;
  status?: QuoteStatus;
}

// ==================== SALES COMPOSITE UPDATE TYPES ====================
export interface SaleLineItemUpdatePayload {
  id: number;
  price?: number | null;
  quantity?: number; // sold quantity
  defects?: number;
  component_id?: number | null;
  sku?: string | null;
  available?: boolean;
  customer_vehicle_id?: number | null;
  invoice_note?: string | null;
  comment?: { comment_text: string };
  new_customer_images_count?: number;
  new_dezshop_images_count?: number;
}

export interface SaleCompositeUpdatePayload {
  line_item_updates?: SaleLineItemUpdatePayload[];
  status?: import('./types').SaleStatus; // reuse enum
  // New: add existing line items from a quote to the sale
  add_line_item_ids?: number[];
  source_quote_id?: string;
  // New: remove existing line items from the sale
  remove_line_item_ids?: number[];
  // New: update sale addresses
  shipping_address_id?: string | number | null;
  invoice_address_id?: string | number | null;
  // New: update supplier
  supplier?: string;
  // New: update company
  company_id?: string | number | null;
  // Commercial discount (fixed amount in sale currency). 0 (with discount_comment=null) clears it.
  discount_amount?: number | null;
  discount_comment?: string | null;
}

/**
 * POST /crm/sales/{hash}/discount — a discount granted AFTER pricing (usually
 * after invoicing and payment). `amount` ADDS to the existing discount. The
 * refund fields are read only when the sale holds more than its new total and
 * the collection is not a Stripe charge (those refund through Stripe).
 */
export interface SaleDiscountPayload {
  amount: number;
  comment: string;
  refund_payment_type?: 'bank_transfer' | 'cash';
  // true → the refund row is 'reimbursed' (money already left); false → 'emitted' (still owed).
  refund_settled?: boolean;
  idempotency_key?: string;
  gateway_reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer';
  // "De returnat clientului" as the operator saw it; the API refuses to run if
  // its own plan gives back a different amount.
  expected_refund_amount?: number;
}

// "Anulare comandă": everything held goes back, the live invoice is reversed in
// full, the sale is cancelled (POST /crm/sales/{hash}/cancel-refund).
export type SaleCancelRefundPayload = Omit<SaleDiscountPayload, 'amount'>;

export interface ShippingOptions {
  open_at_delivery?: boolean;
  saturday_delivery?: boolean;
  insured_amount?: number;
  cash_on_delivery?: boolean;
  delivery_notes?: string;
  pickup_date?: string;          // ISO date string for scheduled pick-up
  pickup_time_interval?: string; // e.g. "08:00-12:00" for the pick-up window
}

// ==================== STATUS ENUMS ====================
// Centralized status definitions for all entities

export enum QuoteStatus {
  PRICE_INFO = 'price_info',
  PRICE_INFO_ADDED = 'price_info_added',
  IN_PROGRESS = 'in_progress',
  INFORMED_NOT_AVAILABLE = 'informed_not_available',
  INFORMED_AVAILABLE = 'informed_available',
  WAITING_CUSTOMER_INFO = 'waiting_customer_info',
  // Cererea e la agentul AI, care informează clientul. Se setează și se
  // eliberează din backend — nu o alege un operator din interfață.
  AGENT_INFORMING_CLIENT = 'agent_informing_client',
  AGENT_PRICE_INFO = 'agent_price_info'
}

export enum TaskStatus {
  NEW = 'new',
  ASSIGNED = 'assigned',
  IN_PROGRESS = 'in_progress',
  BLOCKED = 'blocked',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum SaleStatus {
  NEW = 'new',
  AWAITING_BUYER_CONFIRMATION = 'awaiting_buyer_confirmation',
  COLLECTING = 'collecting',
  PACKING = 'packing',
  READY_TO_SHIP = 'ready_to_ship',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  COMPLETED = 'completed',
  ON_HOLD = 'on_hold',
  CANCELLED = 'cancelled'
}

export enum InvoiceStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PAID = 'paid',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled'
}

export enum ShipmentStatus {
  ESTIMATED = 'estimated',
  CREATED = 'created',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  IN_TRANSIT = 'in_transit',
  DELIVERED = 'delivered',
  RETURNED = 'returned'
}

export enum ReturnStatus {
  NEW = 'new',
  AWAITING_VERIFICATION = 'awaiting_verification',
  AWAITING_REFUND = 'awaiting_refund',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

// Entity type for status configuration
export type StatusEntityType = 'quotes' | 'tasks' | 'sales' | 'invoices' | 'shipments' | 'payments' | 'returns';

// ==================== SALES SCHEMAS ====================
export enum FulfillmentMethod {
  PICKUP_IN_STORE = 'pickup_in_store',
  DELIVERY = 'delivery'
}

export enum PaymentType {
  CASH = 'cash',
  // Stripe / Netopia gateway — money confirmed online before fulfilment.
  CARD_ONLINE = 'card_online',
  // In-person POS terminal at receptie. Reconciled later from BT statement.
  CARD_POS = 'card_pos',
  BANK_TRANSFER = 'bank_transfer',
  CASH_ON_DELIVERY = 'cash_on_delivery'
}

// Mirror of backend `app/stoc/services/payment_types.py` — keep in sync.
export const CARD_PAYMENT_TYPES: ReadonlySet<PaymentType> = new Set([
  PaymentType.CARD_ONLINE,
  PaymentType.CARD_POS
]);

// Customer is physically present at the moment of payment.
export const IN_PERSON_PAYMENT_TYPES: ReadonlySet<PaymentType> = new Set([
  PaymentType.CASH,
  PaymentType.CARD_POS
]);

// Money is collected before fulfilment leaves the warehouse.
export const PREPAID_PAYMENT_TYPES: ReadonlySet<PaymentType> = new Set([
  PaymentType.BANK_TRANSFER,
  PaymentType.CARD_ONLINE,
  PaymentType.CARD_POS
]);

// The money is already ours when the parcel reaches the courier: prepaid
// channels plus cash over the counter, which is how a walk-in pays for a part
// they want shipped. `cash_on_delivery` is absent — that money travels with
// the parcel.
export const SETTLED_BEFORE_DISPATCH_PAYMENT_TYPES: ReadonlySet<PaymentType> = new Set([
  ...PREPAID_PAYMENT_TYPES,
  PaymentType.CASH
]);

// Mirror of backend `BANK_TRANSFER_GATED_SALE_STATUSES` (sales/crud.py). Transferul
// bancar se cere după colectare, la ambalare, deci un transfer neîncasat blochează
// doar trecerea spre expediere. Cardul neconfirmat blochează orice pas.
export const BANK_TRANSFER_GATED_SALE_STATUSES: ReadonlySet<string> = new Set([
  SaleStatus.READY_TO_SHIP,
  SaleStatus.SHIPPED,
  SaleStatus.DELIVERED,
  SaleStatus.COMPLETED
]);

// Mirror of backend `app/stoc/services/payment_statuses.py` — keep in sync.
// Direcția banilor vine din scopul plății, nu din status: pe vânzare, contract
// Kran și factură banii intră, pe retur ies. De aceea vechiul `paid`, care
// însemna amândouă, a fost spart în `collected` și `disbursed`.
export const COLLECTED_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  'collected'
]);

// Banii sunt ai noștri, socotind și un card capturat. Un card se oprește la
// 'gateway_confirmed' și nu ajunge niciodată la 'collected', deci o regulă
// scrisă doar pe încasare raportează 0 pe fiecare comandă EU plătită cu cardul.
export const COLLECTED_OR_CAPTURED_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  ...COLLECTED_STATUSES,
  'gateway_confirmed'
]);

// Statusuri care au sens DOAR pe bani care intră, respectiv DOAR pe bani care
// ies. Restul — 'estimated', 'emitted', 'cancelled', 'error' — sunt neutre și
// stau pe orice plată.
// Oglinda lui `_INBOUND_ONLY_STATUSES` / `_OUTBOUND_ONLY_STATUSES` din backend.
export const INBOUND_ONLY_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  'collected',
  'gateway_confirmed',
  'charged_back'
]);

export const OUTBOUND_ONLY_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  'disbursed'
]);

// Valabilă pe o VÂNZARE, niciodată pe un retur. Pe retur „Rambursată" a însemnat
// dintotdeauna același lucru cu „Achitată" — banii au plecat la client — diferența
// fiind doar metoda, pe care rândul o poartă deja în `payment_type`. Cele 697 de
// rânduri au fost comasate în „Achitată" pe 03.09.2026; setul rămâne ca să nu se
// poată întoarce la două cuvinte pentru același lucru. Pe vânzare are alt sens și
// e singurul corect: noi am returnat banii clientului.
const LEGACY_ON_RETURN_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  'reimbursed'
]);

/**
 * Poate sta statusul ăsta pe o plată cu scopul dat?
 *
 * Registrul de plăți afișează la un loc vânzări, retururi, contracte Kran și
 * creanțe de factură, iar bulina lor e editabilă. Fără filtrul ăsta, un singur
 * click pune „Încasată" pe un retur și inversează direcția banilor în fiecare
 * agregat care îl citește.
 *
 * Scopul necunoscut permite tot: e o gardă împotriva unei direcții greșite, nu
 * un validator general, iar backend-ul refuză oricum cu 400 ce e imposibil.
 */
export function isStatusAllowedForScope(
  scope: string | null | undefined,
  status: string | null | undefined
): boolean {
  const normalized = (status || '').toLowerCase() as PaymentStatus;
  if (!normalized) return true;
  if (scope === 'return') {
    return !INBOUND_ONLY_STATUSES.has(normalized)
      && !LEGACY_ON_RETURN_STATUSES.has(normalized);
  }
  if (scope === 'sale') return !OUTBOUND_ONLY_STATUSES.has(normalized);
  return true;
}

/**
 * Banii unei vânzări sunt încasați? Tratează cardul capturat drept încasat.
 *
 * ⚠️ Spre deosebire de `is_collected` din backend, ăsta NU verifică scopul: nu
 * primește plata întreagă, doar status și tip. Pe o plată de RETUR va răspunde
 * afirmativ, ceea ce e greșit — acolo banii merg în sens invers. Folosește-l
 * doar pe plăți de vânzare. Ecranele de retur au nevoie de altă întrebare.
 */
export function isCollectedStatus(
  status: string | null | undefined,
  paymentType?: string | null
): boolean {
  const normalized = (status || '').toLowerCase() as PaymentStatus;
  return isCardPaymentType(paymentType)
    ? COLLECTED_OR_CAPTURED_STATUSES.has(normalized)
    : COLLECTED_STATUSES.has(normalized);
}

// Oglinda lui `REFUND_ROW_DONE_STATUSES` din backend: rândul de restituire
// și-a făcut treaba — banii au plecat. 'charged_back' e rândul scris de bancă,
// nu de noi: o dispută pierdută, înregistrată din 09.09.2026 ca rând propriu
// care arată spre încasare (S-R2J76Z: 38,51 încasați în payout-ul din august,
// 38,51 retrași în cel din septembrie — două linii de extras, două rânduri).
// E „gata" din clipa în care există: Stripe ne-a debitat deja.
export const REFUND_ROW_DONE_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  'reimbursed',
  'disbursed',
  'charged_back'
]);

/**
 * E rândul ăsta bani care au plecat înapoi la client — o rambursare de-a
 * noastră sau un chargeback luat de bancă? Oglinda lui `is_refund_row` din
 * `payment_statuses.py`.
 *
 * Sumele sunt MEREU pozitive în baza de date; direcția vine din scop și din
 * status, nu din semn. Pe o vânzare „Rambursată” înseamnă rambursare făcută;
 * cât timp o rambursare prin gateway e în zbor („Emisă”) sau a fost refuzată
 * („Eroare”), singurul lucru care o deosebește de o încasare așteptată,
 * respectiv de un card refuzat, e încasarea pe care o inversează
 * (`refunds_payment_id`).
 *
 * Se cheamă doar pe plăți de VÂNZARE. Pe un retur tot scopul e ieșire, deci
 * acolo nu există rând de rambursare — plata însăși e rambursarea.
 */
export function isRefundRow(payment: {
  status?: string | null;
  refunds_payment_id?: string | null;
  return_id?: string | null;
  is_refund?: boolean | null;
} | null | undefined): boolean {
  if (!payment) return false;
  // Ecranele de factură primesc răspunsul gata calculat de backend: acolo
  // `refunds_payment_id` nu e în payload, iar fără el o rambursare în curs nu se
  // deosebește de o încasare așteptată.
  if (typeof payment.is_refund === 'boolean') return payment.is_refund;
  if (payment.return_id) return false;
  if (payment.refunds_payment_id) return true;
  // Un 'charged_back' FĂRĂ legătură e forma veche — încasarea însăși răsturnată
  // de job-ul de dispute — și rămâne o încasare cu banii duși, nu un rând de
  // restituire, până o desparte migrarea.
  return (payment.status || '').toLowerCase() === 'reimbursed';
}

/** Rândul de restituire scris de bancă, nu de noi: o dispută pierdută. */
export function isChargebackRow(payment: Parameters<typeof isRefundRow>[0]): boolean {
  return isRefundRow(payment) && (payment?.status || '').toLowerCase() === 'charged_back';
}

/** Cât mișcă rândul ăsta din banii pe care îi ținem: rambursarea scade. */
export function signedPaymentAmount(payment: {
  amount?: number | string | null;
  status?: string | null;
  refunds_payment_id?: string | null;
  return_id?: string | null;
} | null | undefined): number {
  const value = Number(payment?.amount ?? 0) || 0;
  return isRefundRow(payment) ? -value : value;
}

/**
 * O plată FINALĂ: nu se mai editează suma, tipul sau statusul, deci creionașul
 * dispare. Trei cazuri, oglinda lui `_assert_final_row_untouched` din
 * payments/crud.py (backend-ul refuză oricum, aici doar nu mai oferim butonul):
 *  - reconciliată cu o linie de extras confirmată — suma și tipul sunt ale
 *    extrasului, iar statusul nu mai poate șterge încasarea (un chargeback e
 *    rând propriu, scris de job-ul de dispute, nu un status pe încasare);
 *  - un CARD deja încasat de procesator ('gateway_confirmed') — suma e cea
 *    capturată de Stripe/Netopia. Doar card: 331 de transferuri bancare vii
 *    poartă același status (magazinul RO îl pune indiferent de metodă) și cele
 *    mai multe n-au nicio sumă, deci acolo creionașul e singura cale;
 *  - o rambursare executată de Stripe — are id-ul rambursării și a ajuns la
 *    statusul final. Una încă 'emitted' NU e finală: sync-ul o poate închide
 *    ca 'error', și un operator la fel.
 */
export function isFinalPayment(payment: {
  status?: string | null;
  payment_type?: string | null;
  reconciliations?: unknown[] | null;
  refunds_payment_id?: string | null;
  gateway_reference?: string | null;
} | null | undefined): boolean {
  if (!payment) return false;
  if ((payment.reconciliations || []).length > 0) return true;
  const status = (payment.status || '').toLowerCase() as PaymentStatus;
  if (status === 'gateway_confirmed' && isCardPaymentType(payment.payment_type)) return true;
  return Boolean(payment.refunds_payment_id && payment.gateway_reference && REFUND_ROW_DONE_STATUSES.has(status));
}

export function isCardPaymentType(value: string | null | undefined): boolean {
  return CARD_PAYMENT_TYPES.has((value || '').toLowerCase() as PaymentType);
}

export function isInPersonPaymentType(value: string | null | undefined): boolean {
  return IN_PERSON_PAYMENT_TYPES.has((value || '').toLowerCase() as PaymentType);
}

export type PaymentStatus =
  | 'estimated'
  | 'emitted'
  | 'gateway_confirmed'
  | 'reimbursed'
  // Chargeback: collected, then taken back by the bank after a lost card
  // dispute. Since 09.09.2026 a money-back row of its own (see `isRefundRow`),
  // like a refund — only the bank wrote it, not us.
  | 'charged_back'
  | 'cancelled'
  | 'error'
  // Direcția banilor: `collected` pe vânzare/Kran/factură (bani IN), `disbursed`
  // pe retur (bani OUT). Au înlocuit vechiul `paid`, care însemna amândouă.
  | 'collected'
  | 'disbursed';

export interface PaymentRead {
  id: number;
  payment_series: string | null;
  payment_number: number | null;
  payment_type: PaymentType;
  status: PaymentStatus;
  amount: number;
  currency_id?: number;
  created_at: string;
}

export interface ShipmentRead {
  id: string;
  provider_id?: number;
  provider_name: string;
  tracking_number: string | null;
  status: string;
  shipped_at: string | null;
  delivered_at: string | null;
  supplier?: string;
  package_details?: PackageCreate[];
  estimated_package_details?: PackageCreate[];
  shipping_options?: ShippingOptions;
  // SHIPPING COST — two figures:
  //   estimated_shipping_cost = the shipping price shown to the customer at checkout.
  //   actual_shipping_cost    = what the COURIER charged US, set at AWB (our real cost).
  // HARD RULE: customer-facing amounts (sale total, "Cost livrare", refund prefill,
  // remaining-to-pay) must reflect what the customer was CHARGED — which is
  // payment-model-aware, NOT a blanket "always estimated":
  //   • EU (dezShop Europa, EUR, prepaid card): card charged estimated → use estimated.
  //   • RO (RON, ramburs/COD): customer pays courier cost at delivery → keep actual-first.
  // Gate on sale.currency_id === 3 (EUR). actual is also shown to staff as the internal
  // "Real:" line. WHY: billing the FedEx cost on an EU order made its total drift from
  // the captured card payment.
  estimated_shipping_cost?: number;
  actual_shipping_cost?: number | null;
  fee_payer?: 'supplier' | 'customer' | null;
  return_order_number?: string | null;
  partner_status?: string | null;
  awb_print_count?: number;
  awb_last_printed_at?: string | null;
  awb_last_printed_by_user_id?: number | null;
}

export interface ShipmentUpdate {
  status?: 'estimated' | 'created' | 'shipped' | 'in_transit' | 'delivered' | 'returned' | 'cancelled';
  return_order_number?: string | null;
  tracking_number?: string | null;
  fee_payer?: 'supplier' | 'customer' | null;
  partner_status?: string | null;
  // Extended fields for FE edit flow
  provider_id?: number;
  estimated_package_details?: {
    weight: number;
    length?: number;
    width?: number;
    height?: number;
    package_type_id?: number;
  }[];
  shipping_options?: {
    open_on_delivery?: boolean;
    open_at_delivery?: boolean;
    saturday_delivery?: boolean;
    insured_amount?: number;
    delivery_notes?: string;
  };
  estimated_shipping_cost?: number;
}

export interface InvoiceRead {
  id: number;
  invoice_series: string;
  invoice_number: number;
  pdf_url: string | null;
  status: 'created' | 'reversed' | 'cancelled';
  storno_invoice_number?: number | null;
  // 'discount_credit_note': partial storno for a discount granted after
  // invoicing — listed next to the invoice, never treated as one.
  // 'partial_storno': the same for parts taken out / cheaper transport.
  kind?: 'invoice' | 'discount_credit_note' | 'storno' | 'partial_storno';
  // Signed total of a credit note (−discount); null on a plain invoice.
  face_value?: number | null;
  created_at: string;
}

// For list views, a more compact representation
export interface SaleListItem {
  id: string;
  status: SaleStatus;
  fulfillment_method: FulfillmentMethod;
  supplier?: string;
  // Optional partner integration fields
  sale_partner_id?: number;
  sale_partner_order_id?: string | null;
  sale_partner_name?: string | null;
  partner_order_url?: string | null;
  discount_amount: number;
  discount_comment?: string | null;
  currency_id?: number;
  created_at: string;
  created_by_user_id?: number;
  created_by_username?: string;
  contact: ContactRead; // Assuming ContactRead is sufficient here
  /** What the order comes to — counts 'emitted'/'estimated' payments. */
  amount_paid: number;
  /** What is actually in our hands: 'collected', or a captured card
   *  ('gateway_confirmed'), net of succeeded refunds. Use this for any claim
   *  about money we hold. */
  amount_collected: number;
  payment_count?: number;
  shipment_count?: number;
  invoice_count?: number;
  // Sale-level comments (same shape as line item comments)
  comments?: LineItemComment[];
}

// Interface for sale detail line items with sold quantity
export interface SaleLineItemRead {
  line_item: LineItemFullRead;
  sold_quantity: number;
}

// Swap return line item (for displaying swap returns on sale details)
export interface SwapReturnLineItemRead {
  line_item: LineItemFullRead;
  quantity: number;
}

// Swap return summary (returns created as part of a swap order)
export interface SwapReturnRead {
  return_id: number;
  return_hash: string;
  original_sale_id: number;
  original_sale_hash: string;
  line_items: SwapReturnLineItemRead[];
  total: number;
  status: string;
  created_at: string;
}

// For the detailed sale view
export interface SaleDetailRead extends SaleListItem {
  updated_at?: string;
  created_by_user_id?: number;
  company?: CompanyRead; // Assuming CompanyRead is the detailed company schema
  quote_id?: string;
  // Now each item includes its sold quantity
  line_items: SaleLineItemRead[];
  payments: PaymentRead[];
  shipments: ShipmentRead[];
  invoices: InvoiceRead[];
  shipping_address?: AddressRead | null;
  invoice_address?: AddressRead | null;
  source_name?: string | null;
  // Sale-level comments (same shape as line item comments)
  comments?: LineItemComment[];
  // Swap returns: returns created as part of a swap order where THIS sale is the destination
  swap_returns?: SwapReturnRead[];
  // SmartBill documents a counter flow could not issue; the sale was saved anyway.
  document_warnings?: string[] | null;
}

export interface SaleWithContactRead extends SaleListItem {
  contact: ContactRead;
  company?: CompanyRead;
}

// ==================== RETURNS SCHEMAS ====================
export interface ReturnLineItemRead {
  id: number;                           // ID of the join record
  line_item: LineItemFullRead;         // Nested line item details
  return_quantity: number;             // Number of units returned
}

// Base sale object for returns (omits nested contact/company)
export interface SaleBase {
  id: string;
  status: SaleStatus;
  fulfillment_method: FulfillmentMethod;
  discount_amount: number;
  discount_comment?: string | null;
  created_at: string;
  updated_at: string;
  created_by_user_id?: number;
  quote_id?: string;
  sale_partner_id?: number;
  sale_partner_order_id?: string;
  supplier?: string;
}

export interface CrmReturnRead {
  id: string;
  sale: SaleBase;
  contact: ContactRead;
  company?: CompanyRead;
  pickup_address?: AddressRead;
  return_channel: 'in_store' | 'delivery';
  status: ReturnStatus;
  partner_return_id?: string | null;
  partner_return_url?: string | null;
  created_at: string;
  updated_at: string;
  return_line_items: ReturnLineItemRead[];
  invoices: InvoiceRead[];
  payments: PaymentRead[];
  shipping_labels: ShipmentRead[];
  // Return-level comments (same shape as line item comments)
  comments?: LineItemComment[];
  // --- New fields: original forward shipping refund ---
  include_original_shipping_cost?: boolean;
  original_shipping_cost_amount?: number | null;
  original_shipping_cost_currency_id?: number | null;
  original_shipping_cost_shipment_id?: string | null; // hashed shipment id (e.g., SH-...)
  // Amount deducted from the refund for the return shipment (when supplier issues AWB but customer pays).
  return_shipping_cost_amount?: number | null;
  // --- Swap order reference: when this return was created as part of a swap (colet la schimb) ---
  swap_for_sale_hash?: string | null; // hashed sale id of the new sale that triggered this return
}

// For creating a return
export interface ReturnLineItemCreate {
  line_item_id: number;
  return_quantity: number;
  reason: string;
}

export interface CrmReturnCreate {
  sale_id: string;
  contact_id: string;
  company_id?: string;
  pickup_address_id?: string;
  partner_return_id?: string;
  return_channel: 'in_store' | 'delivery';
  return_line_items: ReturnLineItemCreate[];
  payment?: {
    payment_type: PaymentType;
    amount: number;
  };
  shipment?: {
    provider_id: number;
    packages: PackageCreate[];
    estimated_shipping_cost: number;
    fee_payer: 'supplier' | 'customer';
    return_order_number?: string;
    pickup_date: string;
    delivery_hours: string;
    shipping_options?: {
      insured_amount?: number;
    };
  };
  // --- New optional fields for original forward shipping refund ---
  include_original_shipping_cost?: boolean;
  original_shipping_cost_amount?: number;
  original_shipping_cost_currency_id?: number;
  original_shipping_cost_shipment_id?: string; // hashed shipment id
  // Amount deducted from the refund for the return shipment when supplier issues AWB and customer pays.
  return_shipping_cost_amount?: number;
}

// For creating a sale
export interface SaleCreatePayment {
  payment_type: PaymentType;
  amount: number;
  currency_id?: number;
}
// New interface for sale creation payload: link items with sold quantities
export interface SaleLineItemCreate {
  line_item_id: number;
  sold_quantity: number;
}
export interface SaleCreate {
  contact_id: string;
  company_id?: string;
  quote_id?: string;
  fulfillment_method?: FulfillmentMethod;
  discount_amount?: number;
  discount_comment?: string | null;
  currency_id?: number;
  shipping_address_id: number;
  invoice_address_id?: number;
  line_items: SaleLineItemCreate[];
  payment?: SaleCreatePayment;
}

export interface SaleStatusUpdate {
  status: SaleStatus;
  notes?: string;
}

export interface PaymentCreate {
  payment_type: PaymentType;
  // NEGATIVE is legal and intended: money going back to the customer is a second,
  // negative row on the sale, so the original payment keeps the figure the bank
  // actually sent (and stays matched to its reconciliation entry).
  amount: number;
  currency_id?: number;
  // Omitted → the backend creates it 'emitted'. Outbound statuses are rejected on
  // a sale server-side; the drawer already filters them out of the dropdown.
  status?: PaymentStatus;
}

export interface ReceptionLineItemCreate {
  line_item_id: number;
  component_id?: number | null;
  sku?: string | null;
  quantity: number;
  sale_price: number;
  description: string;
  location?: string | null;
  warehouse_area_id?: number | null;
  line_item_type?: 'part_request' | 'vehicle_sale' | 'kran_service_request';
}

export interface ReceptionSaleCreate {
  contact_id: number;
  company_id?: string | null;
  line_items: ReceptionLineItemCreate[];
  supplier: string;
  invoice_address_id: string | null;
  payments: PaymentCreate[];
}

export interface PackageCreate {
  weight: number;
  length: number;
  width: number;
  height: number;
  package_type_id?: number;
}

export interface ShipmentRecipientCreate {
  name: string;
  phone: string;
  county_id: number;
  city_id: number;
  address_line1: string;
}

export interface OnlineOrderCreate {
  contact_id: string;
  company_id?: string | null;
  line_item_ids: number[];
  supplier: string;
  discount_amount?: number;
  discount_comment?: string | null;
  shipping_address_id: string | null;
  invoice_address_id: string | null;
  payments: PaymentCreate[];
  shipment: {
    provider_id: number;
    packages: PackageCreate[];
    fee_payer?: 'supplier' | 'customer';
    estimated_shipping_cost?: number;
    shipping_options?: ShippingOptions;
      };
  swap_return_items?: {
    line_item_id: number;
    quantity: number;
    original_sale_id: string;
    reason: string;
  }[];
  }

export interface PickupReservationCreate {
  contact_id: string;
  company_id?: string | null;
  quote_id?: string | null;
  line_item_ids: number[];
  supplier: string;
  status?: SaleStatus | 'collecting';
  invoice_address_id?: string | null;
}
